import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Services, Loader, Container, i18n, tokens, localizationMaps } from "@simplybenefits/sb-ui-library";
import { getSignupData, getWhitelabel, signup, updateFormField, resetSignup } from '../../actions/signup.js';
import EmailPasswordForm from './Forms/EmailPasswordForm.js';
import UserDetailsForm from './Forms/UserDetails.js';
import Dependents from './Forms/Dependents.js';
import Beneficiaries from './Forms/Beneficiaries.js';
import Trustee from './Forms/Trustee.js';
import SpendingAccounts from './Forms/SpendingAccounts.js';
import Signage from './Forms/Signage.js';
import ModularFlexSelection from './Forms/ModularFlexSelection/index.js';
import BankingDetails from './Forms/BankingDetails.js';
import { globalStyles } from '../../theme/index.js';
import NavigationContainer from './Components/NavigationContainer.js';
import NavigationItem from './Components/NavigationItem.js';
import * as notifier from '../../actions/notifier.js';
import logoFullColorMedium from '../../assets/logo-full-color-medium.png';
import { removeEnrollmentData, getUserLanguage, setUserLanguage } from '../../services/session.js';

class Signup extends PureComponent {
    state = {
        loading: true,
        activeStep: 0,
        highestStepSeen: 0,
        userDetails: null,
        dependents: [],
        beneficiaries: {
            contingent: [],
            primary: []
        },
        trustee: null,
        spendingAccounts: null,
        modularFlexSelection: null,
        bankingDetails: null,
        signage: {},
        initData: null,
        sections: [],
        saved: {
            emailPasswordForm: false,
            userDetails: false,
            dependents: false,
            beneficiaries: false,
            trustee: false,
            spendingAccounts: false,
            modularFlexSelection: false
        },
        tips: {
            passwordBreakdown: true,
            coverageBreakdown: true,
            enrolledElseWhere: true,
            dependentBreakdown: true,
            dependentEmail: true,
            addAnotherDependent: true,
            beneficiaryBreakdown: true,
            beneficiaryTypeBreakdown: true,
            contingentBeneficiaryBreakdown: true,
            totalShares: true,
            addAnotherBeneficiary: true,
            addAnotherContingentBeneficiary: true,
            trusteeBreakdown: true,
            spendingAccountBreakdown: true,
            hsaBreakdown: true,
            lsaBreakdown: true,
            confirmBreakdown: true
        },
        stripeStatus: this.props.match.params.stripeStatus,
        selectedLanguage: "en"
    };

    _saveInitialData = async (reset = false) => {
        const userID = this.props.match.params.userID;
        const accountID = this.props.match.params.accountID;

        await this.props.dispatch(getWhitelabel(accountID));

        // try to get a stored language if the user is picking up midway through the enrolment process
        const storedLanguage = getUserLanguage();

        if (userID && accountID) {
            const data = await this.props.dispatch(getSignupData(userID, accountID));

            // set the stored language in the first call to get data
            if (!storedLanguage) {
                setUserLanguage(data?.initData?.language || "en");
            }

            if (!reset) { // don't set the language again on a toggle language call
                const language = storedLanguage || data?.initData?.language || "en";
                i18n.setLocale(language);
                await this.setState({ selectedLanguage: language });
            }

            if (data.initData) {
                const sections = [
                    'Create Account',
                    'Details',
                    'Dependents'
                ];

                if (data.initData.requiresBeneficiaries === true) {
                    sections.push('Beneficiaries');
                    sections.push('Trustee');
                }

                if (data.initData.isModular === 1) {
                    sections.push('Plan Selection');
                }

                if (data.initData.spendingAccount) {
                    sections.push('Spending Accounts');
                }

                // if it's a member payment account then Banking Details is required
                if (data.initData.divisionPaymentType && data.initData.divisionPaymentType.toLowerCase() === 'member') {
                    sections.push('Banking Details');
                }

                sections.push('Confirmation');

                const isStripeSuccess = this.props.match.params.stripeStatus === 'success';
                const isStripeCancel = this.props.match.params.stripeStatus === 'cancel';
                const isStripe = isStripeSuccess || isStripeCancel;
                const bankingDetails = isStripeCancel ? null : data.savedData ? data.savedData.bankingDetails : null;

                this.setState({
                    loading: false,
                    activeStep: reset ? 0 : isStripe ? (isStripeSuccess ? sections.length - 1 : sections.length - 2) : 0,
                    highestStepSeen: reset ? 0 : isStripe ? (isStripeSuccess ? sections.length - 1 : sections.length - 2) : 0,
                    saved: this.props.saved || {
                        ...this.state.saved,
                        spendingAccounts: !data.initData.spendingAccount, // if no spending account is provided, set the spending account section to saved
                        modularFlexSelection: !data.initData.isModular,
                        bankingDetails: data.initData.accountPaymentType !== 'member'
                    },
                    initData: data.initData,
                    sections,
                    ...data.savedData,
                    bankingDetails
                });
            }
        } else {
            this.setState({
                loading: false
            });
        }
    };

    async componentDidMount() {
        // check if the user is opening on mobile
        if ((/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i).test(navigator.userAgent)) {
            alert(i18n.t(tokens.labels.signup.enrolmentExperienceBestOnDesktopComputer));
        }

        await this._saveInitialData(); 
    }

    _hideTip = (type) => {
        this.setState({
            tips: {
                ...this.state.tips,
                [type]: false
            }
        });
    };

    /**
     * Toggle the language by setting the localStorage, and then removing all stored data so that translated words need to be reentered, then setting the language in state
     *
     * @async
     * @param {string} language - The language code to set for the user interface.
     * @returns {Promise<void>} A Promise that resolves once language toggling and related actions are complete.
     */
    _toggleLanguage = async (language) => {
        i18n.setLocale(language);
        setUserLanguage(language);

        removeEnrollmentData(this.props.match.params.userID);
        await this.props.dispatch(resetSignup());

        await this._saveInitialData(true);
        await this.setState({ selectedLanguage: language });
    };

    /**
     * Pass this method to each tab view, store the data returned from each tab
     * @param data
     * @param step
     * @param isFinish
     * @private
     */
    _stepCompletion = async (data, step, isFinish = false) => {
        // if we are moving on from the mod flex section handle the new Spending Account data
        if (step === 'modularFlexSelection') {
            const blueprint = this.state.initData.blueprint;
            const spendingAccountData = blueprint.health[data.health].spendingAccounts;
            const hasPooledSpendingAccount = spendingAccountData.isPooled;

            const currentSections = this.state.sections;
            const spendingAccountSectionIndex = currentSections.indexOf('Spending Accounts');

            // update the members spending account data to the data from the tier they selected
            await this.setState({
                initData: {
                    ...this.state.initData,
                    spendingAccount: spendingAccountData
                }
            });

            if (spendingAccountSectionIndex >= 0 && !hasPooledSpendingAccount) {
                currentSections.splice(spendingAccountSectionIndex, 1);

                await this.setState({
                    sections: [ ...currentSections ],
                    saved: {
                        ...this.state.saved,
                        spendingAccounts: true
                    }
                });
            } else if (spendingAccountSectionIndex === -1 && hasPooledSpendingAccount) {
                // if the members current class does not have a Flex SA but their new class does then set the Spending Account tab
                const modflexSectionIndex = currentSections.indexOf('Plan Selection');
                currentSections.splice(modflexSectionIndex + 1, 0, 'Spending Accounts');

                await this.setState({
                    activeStep: modflexSectionIndex,
                    sections: [ ...currentSections ],
                    saved: {
                        ...this.state.saved,
                        spendingAccounts: false
                    }
                });
            }
        }

        if (step === 'userDetails') {
            await this.setState({
                [step]: {
                    ...this.state[step],
                    ...data
                }
            });
        } else {
            await this.setState({
                [step]: data
            });
        }

        if (isFinish) {
            this._finishSignup();
        } else {
            this._handleNext();
        }
    };

    /**
     * Once all steps are complete call this method to send the data to the API and to create user
     * @private
     */
    _finishSignup = async () => {
        // ensure all sections have been saved:
        let missingSection = null;
        const promises = Object.keys(this.state.saved).map((section) => {
            if (!this.state.saved[section]) {
                missingSection = section;
            }

            return null;
        });

        await Promise.all(promises);

        if (missingSection) {
            switch (missingSection) {
                case 'emailPasswordForm':
                    this.setState({
                        activeStep: 0,
                        highestStepSeen: 0
                    });

                    break;
                case 'userDetails':
                    this.setState({
                        activeStep: 1,
                        highestStepSeen: 1
                    });

                    break;
                case 'dependents':
                    this.setState({
                        activeStep: 2,
                        highestStepSeen: 2
                    });

                    break;
                case 'beneficiaries':
                    this.setState({
                        activeStep: 3,
                        highestStepSeen: 3
                    });

                    break;
                case 'trustee':
                    this.setState({
                        activeStep: 4,
                        highestStepSeen: 4
                    });

                    break;

                case 'spendingAccounts':
                    this.setState({
                        activeStep: 5,
                        highestStepSeen: 5
                    });

                    break;

                case 'modularFlexSelection':
                    this.setState({
                        activeStep: 6,
                        highestStepSeen: 6
                    });

                    break;

                case 'bankingDetails':
                    this.setState({
                        activeStep: 7,
                        highestStepSeen: 7
                    });

                    break;
                default:
                    this.setState({
                        activeStep: 0,
                        highestStepSeen: 0
                    });
            }

            this.props.dispatch(notifier.displayError({ verbiage: i18n.t(tokens.labels.signup.ensureCompletedEntireForm) }));
            return;
        }

        const promises0 = this.state.dependents.map((dependent) => ({
            ...dependent,
            birthdate: Services.dates.formatOutgoingDate(dependent.birthdate),
            cohabDate: Services.dates.formatOutgoingDate(dependent.cohabDate),
            dateOfMarriage: Services.dates.formatOutgoingDate(dependent.dateOfMarriage),
            eligibilityDate: Services.dates.formatOutgoingDate(dependent.eligibilityDate),
            relationship: localizationMaps.toEnglish("relationships", dependent.relationship),
            gender: localizationMaps.toEnglish("genders", dependent.gender),
            primaryBenefits: dependent.primaryBenefits.map((benefit) => localizationMaps.toEnglish('primaryBenefits', benefit))
        }));

        const promises1 = this.state.beneficiaries.primary.map((beneficiary) => ({
            ...beneficiary,
            birthdate: Services.dates.formatOutgoingDate(beneficiary.birthdate),
            relationship: localizationMaps.toEnglish("relationships", beneficiary.relationship),
            type: localizationMaps.toEnglish("beneficiariesOptions", beneficiary.type)
        }));

        const promises2 = this.state.beneficiaries.contingent.map((beneficiary) => ({
            ...beneficiary,
            birthdate: Services.dates.formatOutgoingDate(beneficiary.birthdate),
            relationship: localizationMaps.toEnglish("relationships", beneficiary.relationship)
        }));

        const [ dependents, primaryBeneficiaries, contingentBeneficiaries ] = await Promise.all([ promises0, promises1, promises2 ]);

        let bankingDetails = this.state.bankingDetails;

        // if no payment selection then it's CC
        if (!bankingDetails || !bankingDetails.paymentSelection || bankingDetails.paymentSelection === 'CC') {
            bankingDetails = null;
        }

        const data = {
            userDetails: {
                accountID: this.state.userDetails.accountID,
                userID: this.state.userDetails.userID,
                firstname: this.state.userDetails.firstname,
                lastname: this.state.userDetails.lastname,
                language: this.state.selectedLanguage || "en",
                email: this.state.userDetails.email,
                password: this.state.userDetails.password,
                addressLineOne: this.state.userDetails.addressLineOne,
                addressLineTwo: this.state.userDetails.addressLineTwo || null,
                postalCode: this.state.userDetails.postalCode,
                city: this.state.userDetails.city,
                province: localizationMaps.toEnglish("provinces", this.state.userDetails.province),
                phone: this.state.userDetails.phone,
                birthdate: this.state.userDetails.birthdate,
                maritlStat: localizationMaps.toEnglish("maritalStatuses", this.state.userDetails.maritlStat),
                gender: localizationMaps.toEnglish("genders", this.state.userDetails.gender),
                coverageType: localizationMaps.toEnglish("coverageTypes", this.state.userDetails.coverageType),
                waiveFor: this.state.userDetails.waiveFor,
                hasWaived: this.state.userDetails.hasWaived,
                hasRefused: this.state.userDetails.hasRefused,
                comparableCoverageEmployer: this.state.userDetails.comparableCoverageEmployer || null,
                comparableCoverageCarrier: this.state.userDetails.comparableCoverageCarrier || null,
                comparableCoverageCert: this.state.userDetails.comparableCoverageCert || null
            },
            dependents,
            beneficiaries: {
                primary: primaryBeneficiaries,
                contingent: contingentBeneficiaries
            },
            trustee: this.state.trustee,
            modularFlexSelection: this.state.modularFlexSelection,
            spendingAccounts: this.state.spendingAccounts,
            bankingDetails,
            signage: this.state.signage
        };

        if (bankingDetails === null) {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const stripeSessionID = urlParams.get('session_id'); // stripe session_id

            if (stripeSessionID) {
                data.stripeSessionID = stripeSessionID;
            }
        }

        this.props.dispatch(signup(data));
    };

    _setSavedState = async (section, value) => {
        await this.setState({
            ...this.state,
            saved: {
                ...this.state.saved,
                [section]: value
            }
        });

        await this.props.dispatch(updateFormField('saved', section, value));
    };

    _getStepContent = (index) => {
        if (index === 1) {
            return (
                <UserDetailsForm
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    initData={this.state.initData}
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                />
            );
        }

        if (index === 2) {
            return (
                <Dependents
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    initData={this.state.initData}
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                />
            );
        }

        if (this.state.initData.requiresBeneficiaries === true && index === 3) {
            return (
                <Beneficiaries
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                />
            );
        }

        if (this.state.initData.requiresBeneficiaries === true && index === 4) {
            return (
                <Trustee
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                />
            );
        }

        if (this.state.initData.isModular === 1 && index === this.state.sections.indexOf('Plan Selection')) {
            return (
                <ModularFlexSelection
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    modularFlexSelection={this.state.modularFlexSelection}
                    modularFlexTiers={ this.state.initData.modularFlexTiers }
                    blueprint={ this.state.initData.blueprint }
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                />
            );
        }

        if (this.state.initData.spendingAccount && index === this.state.sections.indexOf('Spending Accounts')) {
            return (
                <SpendingAccounts
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    initData={ this.state.initData.spendingAccount }
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    whitelabel={ this.props.whitelabel }
                />
            );
        }

        if (
            (this.state.initData.divisionPaymentType && this.state.initData.divisionPaymentType.toLowerCase() === 'member' && index === this.state.sections.indexOf('Banking Details'))
            || this.state.initData.isMarketplaceOnly
        ) {
            return (
                <BankingDetails
                    stepCompletion={this._stepCompletion}
                    setSavedState={this._setSavedState}
                    goBack={this._handleBack}
                    data={ this.state.initData }
                    bankingDetails={ this.state.bankingDetails }
                    company={ this.state.initData.company }
                    userID={ this.props.match.params.userID }
                    accountID={ this.props.match.params.accountID }
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    isStripeSuccess={ this.props.match.params.stripeStatus === 'success' }
                    isStripeCancel={ this.props.match.params.stripeStatus === 'cancel' }
                    isMarketplaceOnly={this.state.initData.isMarketplaceOnly}
                />
            );
        }

        if (index === this.state.sections.indexOf('Confirmation')) {
            return (
                <Signage
                    stepCompletion={this._stepCompletion}
                    saved={ this.state.saved }
                    goBack={this._handleBack}
                    hideTip={this._hideTip}
                    tips={this.state.tips}
                    loader={this.props.loader}
                />
            );
        }

        return (
            <EmailPasswordForm
                stepCompletion={this._stepCompletion}
                setSavedState={this._setSavedState}
                goBack={this._handleBack}
                hideTip={this._hideTip}
                tips={this.state.tips}
                userLanguage={ getUserLanguage() || this.state.userDetails.language }
                toggleLanguage={ this._toggleLanguage }
            />
        );
    };

    _handleNext = () => {
        this.setState((state) => ({
            activeStep: state.activeStep + 1,
            highestStepSeen: state.activeStep + 1
        }));
    };

    _handleBack = () => {
        this.setState((state) => ({
            activeStep: state.activeStep - 1
        }));
    };

    _selectSection = (index) => {
        if (index > this.state.highestStepSeen) {
            return;
        }

        this.setState({
            highestStepSeen: index > this.state.highestStepSeen ? index : this.state.highestStepSeen,
            activeStep: index
        });
    };

    render() {
        if (this.state.loading || this.state.initData === null) {
            return null;
        }

        const { whitelabel } = this.props;
        const { sections } = this.state;

        const showBeneficiariesTile = this.state.initData.requiresBeneficiaries === true;

        const styles = {
            content: {
                backgroundColor: '#FFF',
                boxShadow: "0 1px 2px rgba(0,0,0,.15)",
                width: 800,
                margin: '0 auto',
                padding: '40px 0 20px 0',
                marginBottom: 20
            },
            logo: globalStyles.logo,
            formBody: {
                margin: '0 auto 30px auto',
                padding: '0 50px'
            }
        };

        if (this.props.loader?.isLoading) {
            <Loader/>;
        }

        return (
            <Container background={"true"}>
                <div style={{ paddingTop: 20 }}>
                    <img
                        alt={ `${ whitelabel.name || 'Simply Benefits' } Logo` }
                        src={ whitelabel.logo || logoFullColorMedium }
                        style={styles.logo}
                    />
                </div>

                <div style={ styles.content }>
                    <NavigationContainer>
                        <NavigationItem
                            label={ i18n.t(tokens.labels.signup.createAccount) }
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Create Account')}
                            isActive={ this.state.activeStep === sections.indexOf('Create Account') }
                            onClick={ () => this._selectSection(sections.indexOf('Create Account')) }
                            customStyle={{ flex: 1.5 }}
                        />

                        <NavigationItem
                            label={ i18n.t(tokens.titles.details) }
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Details')}
                            isActive={ this.state.activeStep === sections.indexOf('Details') }
                            onClick={ () => this._selectSection(sections.indexOf('Details')) }
                            customStyle={{ flex: 0.8 }}
                        />

                        <NavigationItem
                            label={ i18n.t(tokens.titles.dependents) }
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Dependents')}
                            isActive={ this.state.activeStep === sections.indexOf('Dependents') }
                            onClick={ () => this._selectSection(sections.indexOf('Dependents')) }
                            customStyle={{ flex: 1 }}
                        />

                        {
                            showBeneficiariesTile
                                ? <>
                                    <NavigationItem
                                        label={ i18n.t(tokens.titles.beneficiaries) }
                                        isSeen={ this.state.highestStepSeen >= sections.indexOf('Beneficiaries')}
                                        isActive={ this.state.activeStep === sections.indexOf('Beneficiaries') }
                                        onClick={ () => this._selectSection(sections.indexOf('Beneficiaries')) }
                                        customStyle={{ flex: 1 }}
                                    />

                                    <NavigationItem
                                        label={ i18n.t(tokens.titles.trustee) }
                                        isSeen={ this.state.highestStepSeen >= sections.indexOf('Trustee')}
                                        isActive={ this.state.activeStep === sections.indexOf('Trustee') }
                                        onClick={ () => this._selectSection(sections.indexOf('Trustee')) }
                                        customStyle={{ flex: 0.8 }}
                                    />
                                </>
                                : null
                        }

                        {
                            this.state.initData.isModular === 1 &&
                                <NavigationItem
                                    label={ i18n.t(tokens.labels.signup.planSelection) }
                                    isSeen={ this.state.highestStepSeen >= sections.indexOf('Plan Selection')}
                                    isActive={ this.state.activeStep === sections.indexOf('Plan Selection') }
                                    onClick={ () => this._selectSection(sections.indexOf('Plan Selection')) }
                                    customStyle={{ flex: 1 }}
                                />
                        }

                        {
                            this.state.initData.spendingAccount?.isPooled &&
                                <NavigationItem
                                    label={ i18n.t(tokens.titles.spendingAccounts) }
                                    isSeen={ this.state.highestStepSeen >= sections.indexOf('Spending Accounts')}
                                    isActive={ this.state.activeStep === sections.indexOf('Spending Accounts') }
                                    onClick={ () => this._selectSection(sections.indexOf('Spending Accounts')) }
                                    customStyle={{ flex: 1.5 }}
                                />
                        }

                        {
                            this.state.initData.divisionPaymentType && this.state.initData.divisionPaymentType.toLowerCase() === 'member' &&
                                <NavigationItem
                                    label={ i18n.t(tokens.titles.bankingDetails) }
                                    isSeen={ this.state.highestStepSeen >= sections.indexOf('Banking Details')}
                                    isActive={ this.state.activeStep === sections.indexOf('Banking Details') }
                                    onClick={ () => this._selectSection(sections.indexOf('Banking Details')) }
                                    customStyle={{ flex: 1.5 }}
                                />
                        }

                        <NavigationItem
                            label={ i18n.t(tokens.labels.confirmation) }
                            isSeen={ this.state.highestStepSeen >= sections.indexOf('Confirmation')}
                            isActive={ this.state.activeStep === sections.indexOf('Confirmation') }
                            onClick={ () => this._selectSection(sections.indexOf('Confirmation')) }
                            customStyle={{ flex: 1 }}
                        />
                    </NavigationContainer>

                    <div style={ styles.formBody }>
                        { this._getStepContent(this.state.activeStep) }
                    </div>
                </div>

                <span id="testID" />
            </Container>
        );
    }
}

Signup.propTypes = {
    whitelabel: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    whitelabel: state.whitelabel,
    saved: state.signup.saved
});

export default (connect(mapStateToProps)(Signup));